export const SERVICE_IN_PROGRESS = 'in_progress';
export const SERVICE_COMPLETED = 'completed';
export const SERVICE_PENDING = 'pending';
export const SERVICE_CANCELLED = 'cancelled';

export function coerceServiceStatus(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === SERVICE_IN_PROGRESS || s === 'in_service' || s === 'started') return SERVICE_IN_PROGRESS;
  if (s === SERVICE_COMPLETED || s === 'complete' || s === 'done') return SERVICE_COMPLETED;
  if (s === SERVICE_CANCELLED || s === 'cancel' || s === 'canceled') return SERVICE_CANCELLED;
  return SERVICE_PENDING;
}

export function lineStaffIds(line) {
  const staff = line?.staff || line?.assigned_staff || [];
  if (!Array.isArray(staff)) return [];
  return staff.map((p) => String(p?.id || '').trim()).filter(Boolean);
}

export function lineMatchesStylist(line, stylistId) {
  if (!stylistId) return true;
  const ids = lineStaffIds(line);
  if (!ids.length) return true;
  return ids.includes(String(stylistId));
}

export function ongoingLines(services, stylistId) {
  const list = Array.isArray(services) ? services : [];
  return list.filter((line) => {
    const st = coerceServiceStatus(line?.service_status);
    return st === SERVICE_IN_PROGRESS && lineMatchesStylist(line, stylistId);
  });
}

/** Staff ids linked to a visit (assigned desk staff, stylist_id, service line staff). */
export function visitStaffIds(visit) {
  const ids = new Set();
  const stylistId = String(visit?.stylist_id || '').trim();
  if (stylistId) ids.add(stylistId);
  const assigned = visit?.assigned_staff;
  if (Array.isArray(assigned)) {
    for (const person of assigned) {
      const id = String(person?.id || '').trim();
      if (id) ids.add(id);
    }
  }
  const services = visit?.selected_services;
  if (Array.isArray(services)) {
    for (const line of services) {
      for (const id of lineStaffIds(line)) ids.add(id);
    }
  }
  return ids;
}

/** True when stylist has an in-progress service line (locked only after Start). */
export function visitOccupiesStylist(visit, stylistId) {
  const id = String(stylistId || '').trim();
  if (!visit || !id) return false;
  return ongoingLines(visit.selected_services, id).length > 0;
}

/** Stylist ids busy on other guests (excludes current check-in). */
export function collectOccupiedStylistIds(openVisits, excludeCheckinId) {
  const occupied = new Set();
  for (const visit of openVisits || []) {
    if (!visit?.id) continue;
    if (excludeCheckinId && String(visit.id) === String(excludeCheckinId)) continue;
    for (const staffId of visitStaffIds(visit)) {
      if (visitOccupiesStylist(visit, staffId)) occupied.add(staffId);
    }
  }
  return occupied;
}

export function serviceLineLabel(line, options = {}) {
  const { includeStaff = true } = options;
  const name = String(line?.name || line?.service_name || '').trim() || 'Service';
  if (!includeStaff) return name;
  const staff = Array.isArray(line?.staff) ? line.staff.map((s) => s.name).filter(Boolean).join(', ') : '';
  return staff ? `${name} · ${staff}` : name;
}

function normName(value) {
  return String(value || '').trim().toLowerCase();
}

/** Map pending / analysis lines onto catalog rows so the price-list picker shows them selected. */
export function alignPendingLinesWithCatalog(lines, catalog) {
  const pending = Array.isArray(lines) ? lines : [];
  const services = Array.isArray(catalog) ? catalog : [];
  if (!pending.length) return [];

  const byName = new Map();
  const byId = new Map();
  for (const svc of services) {
    const name = normName(svc?.name);
    if (name && !byName.has(name)) byName.set(name, svc);
    const id = normName(svc?.id);
    if (id) byId.set(id, svc);
  }

  return pending
    .map((line) => {
      if (!line) return null;
      const name = String(line.name || line.service_name || '').trim();
      if (!name) return null;
      const idKey = normName(line.id || line.service_id);
      const catalogSvc = (idKey && byId.get(idKey)) || byName.get(normName(name));
      if (catalogSvc) {
        return {
          id: catalogSvc.id,
          name: catalogSvc.name,
          category: catalogSvc.category || line.category,
          price: line.price || catalogSvc.price,
          amount: line.amount != null ? line.amount : catalogSvc.amount,
          base_service_id: catalogSvc.base_service_id || line.base_service_id,
          option_key: line.option_key,
          option_label: line.option_label,
          service_status: line.service_status,
          analysis_source: line.analysis_source,
        };
      }
      return {
        id: String(line.id || line.service_id || name),
        name,
        price: line.price,
        amount: line.amount,
        category: line.category,
        service_status: line.service_status,
        analysis_source: line.analysis_source,
      };
    })
    .filter(Boolean);
}
